import resources from '../../data/resources.json';
import ResourceGuide from '../../pages/ResourceGuide';
import blogArchive from '../../data/blogArchive.json';
import { headingText } from '../../utils/headingText';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { sanityClient } from '../../api/sanityClient';
import { PortableText } from '@portabletext/react';
import { urlFor } from '../../lib/imageUrlBuilder';
import { Helmet } from 'react-helmet-async';
import Footer from '../Footer';
import { Children, cloneElement, isValidElement } from 'react';
import NotFound from '../../pages/NotFound';


function keepHeadingChildren(children) {
  return Children.map(children, child => {
    if (typeof child === 'string') return headingText(child);
    return isValidElement(child) ? cloneElement(child, {}, keepHeadingChildren(child.props.children)) : child;
  });
}
const articleHeadings = Object.fromEntries(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map(Tag => [Tag, ({ children }) => <Tag>{keepHeadingChildren(children)}</Tag>]));

function SanityBlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let active = true;
    setPost(null);
    setStatus("loading");
    sanityClient
      .fetch(
        `*[_type == "blogPost" && !(_id in path("drafts.**")) && defined(publishedAt) && publishedAt <= now() && slug.current == $slug][0]{
          title,
          archived,
          seo {title, description, socialImage {asset->{url}}},
          author {name},
          publishedAt,
          _updatedAt,
          excerpt,
          description,
          body[],
          asset->{
            _id,
            url
          },
          coverImage {
            asset->{
              url
            },
            alt
          }
        }`,
        { slug }
      )
      .then((data) => { if (active) {setPost(data); setStatus(data ? "ready" : "missing");} })
      .catch((error) => {
        if (active) setStatus('error');
      });
    return () => {active = false;};
  }, [slug]);

  if (status === "missing") return <NotFound/>;
  if (status === "error") return <><Helmet><meta name="robots" content="noindex, follow"/></Helmet><main className="home-wrap home-section"><h1>{headingText("We couldn’t load this article.")}</h1><p>Please try again shortly.</p><button className="action-dark" onClick={() => window.location.reload()}>Try again</button></main><Footer/></>;
  if (!post) {
    return (
      <div className="container mx-auto px-6 py-12">
        Loading...
      </div>
    );
  }

  const archived = post.archived || blogArchive.some(item => item.slug === slug);
  const siteUrl = 'https://syncstrategicdigital.com';
  const canonicalUrl = `${siteUrl}/insights/${slug}`;

  /*
   * Use the Sanity excerpt/description when available.
   * If neither exists, create a basic fallback from the post title.
   */
  const metaDescription =
    post.seo?.description ||
    post.excerpt ||
    post.description ||
    `${post.title} — insights on go-to-market strategy, marketing, sales, and revenue growth from Sync Strategic Digital.`;

  const coverImageUrl = post.coverImage?.asset?.url || null;
  const socialImageUrl = post.seo?.socialImage?.asset?.url || coverImageUrl;
  const seoTitle = post.seo?.title || `${post.title} | Sync Strategic Digital`;

  const publishedDate = post.publishedAt
    ? new Date(post.publishedAt)
    : null;

  const updatedDate = post._updatedAt
    ? new Date(post._updatedAt)
    : publishedDate;

  const formattedDate = publishedDate
    ? publishedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const components = {
    block: articleHeadings,
    marks: {
      link: ({ value, children }) => {
        const url = new URL(value.href || '', `${siteUrl}/`);
        const internal = ['syncstrategicdigital.com', 'www.syncstrategicdigital.com'].includes(url.hostname);
        const href = internal ? `${url.pathname.replace(/^\/blog\//, '/insights/')}${url.search}${url.hash}` : url.href;
        return <a href={href} {...(value.blank ? {target:'_blank', rel:'noopener noreferrer'} : {})}>{children}</a>;
      },
    },
    types: {
      image: ({ value }) => (
        <img
          src={urlFor(value).width(1200).url()}
          alt={value.alt || post.title || 'Sync Strategic blog image'}
          className="my-8 rounded-lg w-full object-cover"
          loading="lazy"
        />
      ),
    },
  };

  return (
    <div>

      {/* SEO */}
      <Helmet>

        <title>{seoTitle}</title>

        <meta
          name="description"
          content={metaDescription.slice(0, 160)}
        />

        <meta
          name="robots"
          content={archived ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"}
        />

        <link
          rel="canonical"
          href={canonicalUrl}
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={seoTitle}
        />

        <meta
          property="og:description"
          content={metaDescription.slice(0, 200)}
        />

        <meta
          property="og:url"
          content={canonicalUrl}
        />

        <meta
          property="og:type"
          content="article"
        />

        <meta
          property="og:site_name"
          content="Sync Strategic Digital"
        />

        <meta
          property="og:locale"
          content="en_US"
        />

        {socialImageUrl && (
          <meta
            property="og:image"
            content={socialImageUrl}
          />
        )}

        {post.publishedAt && (
          <meta
            property="article:published_time"
            content={post.publishedAt}
          />
        )}

        {post._updatedAt && (
          <meta
            property="article:modified_time"
            content={post._updatedAt}
          />
        )}

        {/* Twitter / X */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content={seoTitle}
        />

        <meta
          name="twitter:description"
          content={metaDescription.slice(0, 200)}
        />

        {socialImageUrl && (
          <meta
            name="twitter:image"
            content={socialImageUrl}
          />
        )}

        {/* Article Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: post.title,
            description: metaDescription,
            url: canonicalUrl,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': canonicalUrl,
            },
            author: {
              '@type': 'Person',
              name: post.author?.name || 'Rob Kriner',
              url: `${siteUrl}/about`,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Sync Strategic Digital',
              url: siteUrl,
            },
            ...(coverImageUrl && {
              image: [coverImageUrl],
            }),
            ...(post.publishedAt && {
              datePublished: post.publishedAt,
            }),
            ...(updatedDate && {
              dateModified: updatedDate.toISOString(),
            }),
          })}
        </script>

      </Helmet>

      {archived && <aside className="archive-notice home-wrap" aria-label="Archived article"><strong>From the archive</strong><p>This article reflects earlier corporate marketing work. It is preserved for reference.</p><a href="/insights">Explore nonprofit resources →</a></aside>}
      {/* Cover Image */}
      {coverImageUrl && (
        <div
          className="w-full h-96 bg-cover bg-center"
          style={{
            backgroundImage: `url(${coverImageUrl})`,
          }}
          role="img"
          aria-label={
            post.coverImage?.alt ||
            `${post.title} — Sync Strategic Digital`
          }
        />
      )}

      {/* Article */}
      <article
        className="container mx-auto px-6 py-12 max-w-3xl"
      >

        <header>

          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            {headingText(post.title)}
          </h1>

          <p className="text-gray-600 mb-3">By {post.author?.name || "Rob Kriner"}</p>
          {formattedDate && (
            <time
              dateTime={post.publishedAt}
              className="block text-gray-500 mb-10"
            >
              Published {formattedDate}
            </time>
          )}

        </header>

        <div className="prose prose-custom">
          <PortableText
            value={post.body}
            components={components}
          />
        </div>

      </article>

      <Footer />

    </div>
  );
}

export default function BlogDetail() {
  const { slug } = useParams();
  const guide = resources.find(item => item.slug === slug);
  return guide ? <ResourceGuide guide={guide} /> : <SanityBlogDetail />;
}
